import { Text } from "@overmap-ai/blocks"
import type { AssetType, Stored } from "@overmap-ai/common"
import {
	type ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useMemo } from "react"

import { formatTimestampDate } from "../../../utils/date"
import { AssetTypeIcon } from "../../Icons/AssetTypeIcon"
import { ColumnSortButton, ColumnSortIcon } from "../components"

export const useAssetTypeTableColumns = () => {
	return useMemo<ColumnDef<Stored<AssetType>>[]>(
		() => [
			{
				id: "name",
				accessorKey: "name",
				enableHiding: true,
				meta: {
					plainHeader: "Name",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Name
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ row }) {
					const assetType = row.original

					return (
						<div className="flex items-center gap-2">
							<AssetTypeIcon assetTypeId={assetType.uuid} background={false} />
							<Text>{assetType.name}</Text>
						</div>
					)
				},
			},
			{
				id: "submitted_at",
				accessorFn: (assetType) => assetType.submitted_at,
				meta: {
					plainHeader: "Created at",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Created at
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					return <Text accentColor="base">{formatTimestampDate(getValue<string>())}</Text>
				},
			},
		],
		[],
	)
}

export const useAssetTypeTable = (args: Omit<TableOptions<Stored<AssetType>>, "getCoreRowModel">) => {
	return useReactTable<Stored<AssetType>>({
		...args,
		getRowId: (originalRow) => originalRow.uuid,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})
}
